import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { Check, ChevronRight } from 'lucide-react'
import { ExerciseSummaryRow } from './ExerciseSummaryRow'
import { cn } from '../../lib/utils'
import type { Workout, WorkoutResult } from '../../types/workout'

const EFFORT_DOT_CLASSES = [
  'bg-yellow-400',
  'bg-blue-500',
  'bg-green-500',
  'bg-orange-400',
  'bg-red-500',
]

function EffortDots({ effort }: { effort: number }) {
  return (
    <div className="flex gap-0.5 items-center shrink-0">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'size-1.5 rounded-full',
            i < effort ? EFFORT_DOT_CLASSES[effort - 1] : 'bg-muted-foreground/25'
          )}
        />
      ))}
    </div>
  )
}

interface ResultPreviewProps {
  result: WorkoutResult
  /** The day the parent workout is scheduled on; the weekday hint is shown
   *  only when result.completed_on differs from this. May be null for
   *  unscheduled workouts. */
  scheduledDate: string | null
}

function ResultPreview({ result, scheduledDate }: ResultPreviewProps) {
  const { t, i18n } = useTranslation()
  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const showWeekday = result.completed_on !== scheduledDate
  const weekdayLabel = showWeekday
    ? format(parseISO(result.completed_on), 'EEE', { locale: dateFnsLocale })
    : null

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
      {weekdayLabel && (
        <span className="uppercase tracking-wide text-[10px] shrink-0">{weekdayLabel}</span>
      )}
      <EffortDots effort={result.effort} />
      <span className="font-medium text-foreground/80 shrink-0">
        {result.completion_percent}%
      </span>
      {result.note && (
        <span className="truncate text-foreground/60">
          · {result.note}
        </span>
      )}
      <span className="sr-only">
        {t('trainingDiary.workouts.results.completedOn', 'Completed on')} {result.completed_on}
      </span>
    </div>
  )
}

interface WorkoutCardProps {
  workout: Workout
  results?: WorkoutResult[]
  completed?: boolean
  hideResults?: boolean
}

export function WorkoutCard({ workout, results = [], completed = false, hideResults = false }: WorkoutCardProps) {
  const navigate = useNavigate()
  const showResults = !hideResults && results.length > 0

  return (
    <button
      type="button"
      onClick={() => navigate(`/training-diary/workouts/${workout.id}`)}
      className={cn(
        'rounded-md border bg-card text-left transition-colors hover:bg-accent w-full p-2',
        completed && !hideResults && 'border-muted-foreground/20'
      )}
    >
      <ExerciseSummaryRow
        exercise={workout.exercise}
        compact
        className={completed && !hideResults ? 'text-muted-foreground' : undefined}
        right={
          <div className="flex items-center gap-1">
            {completed && !hideResults && <Check className="size-4 text-green-600 dark:text-green-500" />}
            <ChevronRight className="size-4 text-muted-foreground" />
          </div>
        }
      />
      {showResults && (
        <div className="mt-1.5 pl-[52px] flex flex-col gap-1">
          {results.map((r) => (
            <ResultPreview key={r.id} result={r} scheduledDate={workout.scheduled_date} />
          ))}
        </div>
      )}
    </button>
  )
}
